<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ExploreGrid from '~/components/explore/ExploreGrid.vue';
import ExploreGridToggle from '~/components/explore/ExploreGridToggle.vue';
import { useInfiniteScroll } from '~/composables/infinite-scroll';
import { getApiRoot } from '~/utilities/api';
import { BASE_TITLE } from '~/utilities/constants';

const props = defineProps({
  slug: { type: String, required: true },
});

const route = useRoute();
const router = useRouter();

// --- Marketplace Details ---
const {
  data: marketplace,
  pending: marketplacePending,
  error: marketplaceError,
} = await useFetch<any>(`${getApiRoot()}/gardens/${props.slug}`);

const name = computed(() => marketplace.value?.name);
useHead({
  title: `${name.value || 'Unknown'} ${BASE_TITLE}`,
  meta: [
    { property: 'og:title', content: `${name.value || 'Unknown'} ${BASE_TITLE}` },
    { name: 'twitter:title', content: `${name.value || 'Unknown'} ${BASE_TITLE}` },
    { name: 'twitter:card', content: 'summary_large_image' },
    { property: 'og:description', content: marketplace.value?.description },
    { name: 'twitter:description', content: marketplace.value?.description },
  ],
});

// --- Tabs ---
const tabs = computed(() => [
  { id: 'items', title: 'Listings' },
  { id: 'activity', title: 'Activity' },
]);
// Ensure selectedTab is a string, not an array
const routeTab = route.query.tab;
const initialTab = Array.isArray(routeTab) ? routeTab[0] : routeTab;
const selectedTab = ref(initialTab === 'activity' ? 'activity' : 'items');

watch(selectedTab, (newTab) => {
  // Only update query if it changes
  if (route.query.tab !== newTab) {
    router.replace({ query: { ...route.query, tab: newTab } });
  }
});
// Watch for route changes to update the tab (e.g., browser back/forward)
watch(
  () => route.query.tab,
  (newTabQuery) => {
    const newTab = Array.isArray(newTabQuery) ? newTabQuery[0] : newTabQuery;
    const targetTab = newTab === 'activity' ? 'activity' : 'items';
    if (selectedTab.value !== targetTab) {
      selectedTab.value = targetTab;
    }
  }
);

// --- Filters (from Sidebar) ---
const selectedCollectionIds = ref<string[]>([]); // Populated via v-model from sidebar
const selectedProfileIds = ref<string[]>([]); // Populated via v-model from sidebar

// --- Items (NFTs) ---
const gridStyle = useCookie<'grid-compact' | 'grid' | 'table'>('grid-style');
gridStyle.value = gridStyle.value || 'grid';

// Sorting - similar to [id].vue
const routeSorting = route.query?.sort_by;
// Ensure sorting is a string, default to 'xch_price_asc' based on API default
const initialSorting = Array.isArray(routeSorting) ? routeSorting[0] : routeSorting;
const sorting = ref<string>(initialSorting || 'xch_price'); // Default based on NftsByOffersSortBy
const sortOptions = [
  // Based on NftsByOffersSortBy enum in garden.py
  { value: 'xch_price', label: 'Offer: Low to High' },
  { value: '-created_at', label: 'Offer: New to Old' },
  // Add other relevant sort options if the API supports them for gardens
];
watch(sorting, (newSort) => {
  if (route.query.sort_by !== newSort) {
    router.replace({ query: { ...route.query, sort_by: newSort } });
  }
});
watch(
  () => route.query.sort_by,
  (newSortQuery) => {
    const newSort = Array.isArray(newSortQuery) ? newSortQuery[0] : newSortQuery;
    const targetSort = newSort || 'xch_price_asc';
    if (sorting.value !== targetSort) {
      sorting.value = targetSort;
    }
  }
);

// Token ID - similar to [id].vue
const routeToken = route.query.token;
const initialToken = Array.isArray(routeToken) ? routeToken[0] : routeToken;
const selectedTokenId = ref<string>(initialToken || 'xch');
watch(selectedTokenId, (newToken) => {
  if (route.query.token !== newToken) {
    router.replace({ query: { ...route.query, token: newToken } });
  }
});
watch(
  () => route.query.token,
  (newTokenQuery) => {
    const newToken = Array.isArray(newTokenQuery) ? newTokenQuery[0] : newTokenQuery;
    const targetToken = newToken || 'xch';
    if (selectedTokenId.value !== targetToken) {
      selectedTokenId.value = targetToken;
    }
  }
);

// Computed URL for items fetch
const itemsUrl = computed(() => {
  const params = new URLSearchParams();
  params.set('include_unlisted', 'true');
  params.set('sort_by', sorting.value);
  params.set('token_id', selectedTokenId.value);
  // Add collection IDs
  selectedCollectionIds.value.forEach((id) => params.append('collection_ids', id));
  // Add profile IDs
  selectedProfileIds.value.forEach((id) => params.append('profile_ids', id));
  // TODO: Add include_unminted if needed

  return `${getApiRoot()}/gardens/${props.slug}/nfts/by_offers?${params.toString()}`;
});

const itemsBottom = ref<HTMLElement | null>(null);
const {
  items,
  pending: itemsPending,
  error: itemsError,
} = useInfiniteScroll(
  itemsBottom,
  () => itemsUrl.value,
  [sorting, selectedTokenId, selectedCollectionIds, selectedProfileIds] // Dependencies
);

// --- Activity (Events) ---
// TODO: Add state for event type filters if needed
const selectedEventTypes = ref<string[]>([]); // Example: ['0', '2'] for MINT, TRADE

// Computed URL for activity fetch
const activityUrl = computed(() => {
  const params = new URLSearchParams();
  // Add event type filters
  selectedEventTypes.value.forEach((type) => params.append('type', type));

  return `${getApiRoot()}/gardens/${props.slug}/events?${params.toString()}`;
});

const activityBottom = ref<HTMLElement | null>(null);
const {
  items: activity,
  pending: activityPending,
  error: activityError,
} = useInfiniteScroll(
  activityBottom,
  () => activityUrl.value,
  [selectedEventTypes] // Dependencies
);
</script>

<template>
  <div>
    <!-- Marketplace Loading/Error -->
    <div v-if="marketplacePending" class="flex justify-center py-12">
      <LoadingSpinner />
    </div>
    <div v-else-if="marketplaceError" class="py-12 text-center text-red-500">
      Error loading marketplace data.
      <!-- TODO: Improve error display -->
    </div>

    <!-- Marketplace Content -->
    <div v-else-if="marketplace">
      <!-- Banner Image -->
      <div class="relative h-80 w-full">
        <div v-if="marketplace.banner_image_url" class="h-full w-full bg-black">
          <img
            :src="marketplace.banner_image_url"
            :alt="`${marketplace.name} banner`"
            class="h-full w-full object-cover object-center opacity-80"
          />
        </div>
        <div v-else class="bg-header h-full w-full"></div>

        <!-- Centered Content Over Banner -->
        <div class="absolute inset-0 flex flex-col items-center justify-center bg-black/30 pt-16">
          <h1 class="mb-4 text-center text-2xl font-bold text-white md:text-6xl">{{ marketplace.name }}</h1>

          <!-- Creator Avatars -->
          <!-- <div class="flex items-center justify-center rounded-lg lg:mb-6">
            <div class="flex -space-x-4 md:-space-x-6">
              <template v-for="(profile, index) in marketplace.profiles?.slice(0, 5)" :key="profile.id">
                <NuxtLink
                  :to="getUrlForProfile(profile)"
                  class="relative h-12 w-12 overflow-hidden rounded-full bg-white dark:bg-neutral-800 md:h-16 md:w-16"
                  :style="{ zIndex: index }"
                >
                  <img
                    v-if="profile.avatar_uri"
                    :src="profile.avatar_uri"
                    :alt="profile.name || 'Creator'"
                    class="h-full w-full object-cover"
                  />
                  <div v-else class="h-full w-full bg-neutral-200 dark:bg-neutral-700"></div>
                </NuxtLink>
              </template>
            </div>
          </div> -->
        </div>
      </div>
      <div class="container mx-auto px-4 py-8">
        <Tabs v-model="selectedTab" :tabs="tabs" class="mb-6">
          <!-- Controls Slot -->
          <template #controls>
            <div v-if="selectedTab === 'items'" class="hidden h-full items-center gap-3 px-3 md:flex">
              <!-- TODO: Add TokenPicker if offers support multiple tokens -->
              <!-- <TokenPicker v-model="selectedTokenId" :collection-id="null" /> -->
              <select
                v-model="sorting"
                class="block w-full rounded-md border-neutral-300 py-2 pl-3 pr-10 text-base focus:border-black focus:outline-none focus:ring-black dark:border-neutral-600 sm:text-sm"
              >
                <option v-for="sortOption in sortOptions" :key="sortOption.value" :value="sortOption.value">
                  {{ sortOption.label }}
                  <!-- TODO: i18n -->
                </option>
              </select>
              <ExploreGridToggle v-model="gridStyle" />
            </div>
            <!-- TODO: Add controls for Activity tab if needed (e.g., event type filter dropdown) -->
          </template>
        </Tabs>

        <!-- Items Tab -->
        <div v-if="selectedTab === 'items'" class="flex flex-col gap-6 lg:flex-row">
          <GardenFilterSidebar
            :collections="marketplace.collections"
            :profiles="marketplace.profiles"
            v-model:selected-collection-ids="selectedCollectionIds"
            v-model:selected-profile-ids="selectedProfileIds"
            :slug="slug"
            class="w-full shrink-0 lg:w-56"
          />
          <div class="min-w-0 flex-1">
            <!-- Mobile Controls for Items -->
            <div class="mb-4 flex items-center gap-3 md:hidden">
              <select
                v-model="sorting"
                class="block w-full rounded-md border-neutral-300 py-2 pl-3 pr-10 text-base focus:border-black focus:outline-none focus:ring-black dark:border-neutral-600 sm:text-sm"
              >
                <option v-for="sortOption in sortOptions" :key="sortOption.value" :value="sortOption.value">
                  {{ sortOption.label }}
                  <!-- TODO: i18n -->
                </option>
              </select>
              <ExploreGridToggle v-model="gridStyle" />
            </div>

            <!-- Items Grid -->
            <ExploreGrid :nfts="items" :pending="itemsPending" :grid-style="gridStyle" />
            <!-- Infinite Scroll Trigger -->
            <div ref="itemsBottom" class="h-10">
              <LoadingSpinner v-if="itemsPending" small class="mx-auto" />
            </div>
            <!-- Error Display -->
            <div v-if="itemsError" class="py-12 text-center text-red-500">
              Error loading items. {{ itemsError.message }}
            </div>
            <!-- No Items Found -->
            <div v-if="!itemsPending && items.length === 0 && !itemsError" class="py-12 text-center text-neutral-500">
              No items found matching your criteria.
            </div>
          </div>
        </div>

        <!-- Activity Tab -->
        <div v-else-if="selectedTab === 'activity'">
          <!-- TODO: Add Event Type Filters UI -->
          <!-- <EventTypeFilter v-model="selectedEventTypes" /> -->

          <!-- Activity List -->
          <EventsTable :events="activity" :pending="activityPending" />
          <!-- Infinite Scroll Trigger -->
          <div ref="activityBottom" class="h-10">
            <LoadingSpinner v-if="activityPending" small class="mx-auto" />
          </div>
          <!-- Error Display -->
          <div v-if="activityError" class="py-12 text-center text-red-500">
            Error loading activity. {{ activityError.message }}
          </div>
          <!-- No Activity Found -->
          <div
            v-if="!activityPending && activity.length === 0 && !activityError"
            class="py-12 text-center text-neutral-500"
          >
            No activity found.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bg-header {
  background-color: #262626;
  background-image: url('/bg-header.svg');
}
</style>
